import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getPostHogMetrics } from '@/services/composer-dashboard/get-posthog-metrics';
import { PostHogMetricsResponse } from '@/types/composer-dashboard';
import { useOrgStore } from '@/stores/org-store';

/**
 * Hook to fetch PostHog analytics metrics for the composer dashboard.
 *
 * Returns web analytics data (visitors, pageviews, sessions, timeseries,
 * top pages, activity heatmap) filtered to the organization's URLs.
 *
 * @param days - Number of days to look back (1-90, defaults to 30)
 * @param options - Additional react-query options
 */
export const useGetPostHogMetrics = (
  days: number = 30,
  options?: Omit<
    UseQueryOptions<PostHogMetricsResponse, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;

  return useQuery<PostHogMetricsResponse, Error>({
    queryKey: ['composer-posthog-metrics', orgId, days],
    queryFn: () => getPostHogMetrics(orgId!, days),
    staleTime: 60 * 1000, // 1 minute - PostHog data is live
    ...options,
    enabled: !!orgId && (options?.enabled ?? true),
  });
};
